// react-table sortType that orders numbers as numbers and dates as dates, and
// otherwise falls back to react-table's own alphanumeric ordering (AC-15842).
//
// WHY: react-table's default is `alphanumeric` for every column, which splits
// values on digit groups and parseInts each chunk. That gets decimals and
// negatives wrong — 1.5 sorts before 1.25 (chunk "5" vs "25"), and -5 before -10
// — and its toString() returns "" for anything that is not a number or string,
// so a column holding Date objects gets no ordering at all. The column menu now
// labels numeric/date columns as such (columnTypeOf), so the ordering has to
// actually match the promise.
//
// Classification is per compared PAIR, not per column, so a column holding a few
// stray non-numeric values still orders its numeric values correctly and only
// falls back where it must. Blanks always sort last regardless of direction —
// react-table applies `desc` by reversing our result, so returning a constant
// here would flip them; instead we keep them out of the numeric/date branches
// and let the alphanumeric fallback place them, matching existing behaviour.
//
// Only affects clientSide tables. Server tables set manualSortBy, so react-table
// never sorts — the database does, and it already orders numerically/by date.

import { isBlank } from "./columnTypeOf";

const NUMERIC_RE = /^-?\d+(\.\d+)?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]|$)|^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
const reSplitAlphaNumeric = /([0-9]+)/gm;

// Numeric value of `v`, or null when it isn't cleanly numeric.
function asNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const t = v.trim();
  return NUMERIC_RE.test(t) ? Number(t) : null;
}

// Epoch ms for `v`, or null when it isn't recognisably a date. Mirrors
// columnTypeOf's narrow patterns so labels and ordering agree.
function asDate(v) {
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!DATE_RE.test(t)) return null;
  const parsed = Date.parse(t);
  return Number.isNaN(parsed) ? null : parsed;
}

// Verbatim port of react-table 7.7.0's `alphanumeric` sortType (its sortTypes
// are not exported). Text columns must keep sorting exactly as they do today —
// this file changes ordering ONLY for pairs that are both numeric or both dates.
function toString(a) {
  if (typeof a === "number") {
    if (Number.isNaN(a) || a === Infinity || a === -Infinity) return "";
    return String(a);
  }
  if (typeof a === "string") return a;
  return "";
}

function alphanumeric(aRaw, bRaw) {
  const a = toString(aRaw).split(reSplitAlphaNumeric).filter(Boolean);
  const b = toString(bRaw).split(reSplitAlphaNumeric).filter(Boolean);

  while (a.length && b.length) {
    const aa = a.shift();
    const bb = b.shift();
    const an = parseInt(aa, 10);
    const bn = parseInt(bb, 10);
    const combo = [an, bn].sort();

    // Both are strings
    if (Number.isNaN(combo[0])) {
      if (aa > bb) return 1;
      if (bb > aa) return -1;
      continue;
    }
    // One is a string, one is a number
    if (Number.isNaN(combo[1])) return Number.isNaN(an) ? -1 : 1;
    // Both are numbers
    if (an > bn) return 1;
    if (bn > an) return -1;
  }

  return a.length - b.length;
}

export default function smartSortType(rowA, rowB, columnId) {
  const a = rowA?.values?.[columnId];
  const b = rowB?.values?.[columnId];

  // Let the fallback place blanks, so their position is unchanged from today.
  if (!isBlank(a) && !isBlank(b)) {
    const an = asNumber(a);
    const bn = asNumber(b);
    if (an !== null && bn !== null) return an === bn ? 0 : an > bn ? 1 : -1;

    const ad = asDate(a);
    const bd = asDate(b);
    if (ad !== null && bd !== null) return ad === bd ? 0 : ad > bd ? 1 : -1;
  }

  return alphanumeric(a, b);
}
